// Return trip feature (docs/RETURNTRIP.md).
//
// Client-side rules for the return trip offer, ported from the iOS/Android apps
// (ReturnTripSuggestionResolver, ReturnTripPlacedBookingTracker, ReturnTripDismissalStore).
// The backend decides whether a booking is eligible at all via `returnTripRules`; on top of
// that the client only offers for a booking the user just placed, does not interrupt an
// ongoing direct trip and never re-offers something the user already declined.

import { emitCustomEvent } from 'react-custom-events'
import { getBooking } from '~/lib/api'
import { hasEnded } from '~/utils/data'

const DISMISSED_KEY = 'returnTripDismissedBookingId'

// Statuses for a direct booking that is actively in progress, up to and including the
// customer being in the car. Mirrors APIBookingStatus.StatusCode.isOngoingUpToCustomerInCar.
const ONGOING_DIRECT_CODES = [110, 120, 121, 310, 315, 320, 410]

// In memory on purpose and answered only once per booking, so an offer can neither
// survive a reload nor come back the next time the same booking's details are opened.
let justPlacedBookingId = null

export const isReturnTripEnabled = () => typeof CONFIG !== 'undefined' && CONFIG.return_trip === true

export const markBookingPlaced = bookingId => {
  justPlacedBookingId = bookingId
}

// True if bookingId is the booking that was just placed, which is then forgotten.
export const consumeJustPlaced = bookingId => {
  // Ids arrive as numbers from the booking response but may be strings from the store
  const wasJustPlaced = justPlacedBookingId !== null && String(justPlacedBookingId) === String(bookingId)
  if (wasJustPlaced) justPlacedBookingId = null
  return wasJustPlaced
}

// Only one dismissed booking is remembered at a time, like the apps.
export const dismissReturnTripOffer = bookingId => {
  try { localStorage.setItem(DISMISSED_KEY, String(bookingId)) } catch (e) { /* storage unavailable */ }
}

export const isReturnTripOfferDismissed = bookingId => {
  try { return localStorage.getItem(DISMISSED_KEY) === String(bookingId) } catch (e) { return false }
}

// The automatic offer follows the backend's returnTripRules.
export const isEligibleForReturnTripOffer = booking =>
  booking?.returnTripRules?.shouldShowReturnTripOption === true && !!booking?.destinationLocation

// The "Book return trip" buttons (booking details, booking rows) only need somewhere to
// return from; the backend flag is deliberately not consulted there.
export const canStartReturnTrip = booking => isReturnTripEnabled() && !!booking?.destinationLocation

// A booking without bookingType (possible in the store's list) only counts once a car is
// involved; a waiting booking of unknown type is more likely a prebooking than a direct trip.
const CAR_INVOLVED_CODES = [310, 315, 320, 410]

export const hasOngoingDirectBooking = bookings =>
  (bookings || []).some(booking => {
    const code = Number(booking?.bookingStatus?.statusCode)
    if (booking?.bookingType === 'PRE_BOOKING') return false
    if (!booking?.bookingType) return CAR_INVOLVED_CODES.includes(code)
    return ONGOING_DIRECT_CODES.includes(code)
  })

const skip = reason => {
  console.warn(`[ReturnTrip] offer skipped: ${reason}`)
  return null
}

/**
 * Decides whether the return trip offer should be shown when the details of
 * `closedBooking` close. Resolves to the booking to offer for, or null.
 */
export async function resolveReturnTripSuggestion(closedBooking, activeBookings) {
  if (!isReturnTripEnabled()) return skip('feature disabled')
  if (!closedBooking?.id) return skip('no booking to evaluate')
  if (!consumeJustPlaced(closedBooking.id)) return skip(`booking ${closedBooking.id} was not just placed`)
  if (isReturnTripOfferDismissed(closedBooking.id)) return skip(`booking ${closedBooking.id} was dismissed earlier`)
  if (hasEnded(closedBooking)) return skip(`booking ${closedBooking.id} has ended`)

  // The store's copy of the closed booking may lack bookingType (status polling merges);
  // use the type BookingDone already resolved for it.
  const bookings = (activeBookings || []).map(booking =>
    booking.id === closedBooking.id ? { ...booking, bookingType: booking.bookingType || closedBooking.bookingType } : booking
  )
  if (hasOngoingDirectBooking(bookings)) {
    const ongoing = bookings.filter(b => hasOngoingDirectBooking([b])).map(b => `${b.id} (${b.bookingType || 'type unknown'}, status ${b.bookingStatus?.statusCode})`)
    return skip(`a direct booking is ongoing: ${ongoing.join(', ')}`)
  }

  // Prefer the freshest copy of the booking, which carries the backend's latest rules.
  let booking = bookings.find(item => item.id === closedBooking.id) || closedBooking
  if (!booking.returnTripRules) {
    try {
      booking = { ...booking, ...(await getBooking(booking.id)) }
    } catch (error) {
      console.error('Could not refresh booking for return trip offer', error)
    }
  }
  if (!isEligibleForReturnTripOffer(booking)) {
    return skip(`booking ${booking.id} not eligible (shouldShowReturnTripOption=${booking.returnTripRules?.shouldShowReturnTripOption}, destination=${!!booking.destinationLocation})`)
  }
  return booking
}

/**
 * Opens the prefilled return trip sheet for `booking`. Refreshes the booking first so
 * the tracking reference (returnTripRules.originalBookingReference) is current.
 */
export async function startReturnTripBooking(booking) {
  let fresh = booking
  try {
    fresh = { ...booking, ...(await getBooking(booking.id)) }
  } catch (error) {
    console.error('Could not refresh booking for return trip', error)
  }
  if (!fresh?.destinationLocation) return
  emitCustomEvent('RETURN_TRIP_BOOKING', fresh)
}
